import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaSearch } from 'react-icons/fa';
import { useJobSearch } from '../../hooks/useJobSearch';
import { JobSearchResult, JobSearchStatus } from '../../types';
import { searchResultRoute } from './searchResultRoute';

import css from './SearchBox.module.scss';

interface SearchBoxProps {
  gridArea?: string;
  gridRow?: number;
  gridColumn?: number;
}

const LISTBOX_ID = 'job-search-listbox';
const optionId = (index: number) => `job-search-option-${index}`;

// Display label + style for each bucket badge. Colours mirror the app's existing
// status palette (amber opportunities, blue applications, grey archive, red
// rejected) used elsewhere in the UI.
const STATUS_BADGE: Record<JobSearchStatus, { label: string; className: string }> = {
  pending: { label: 'Opportunity', className: 'badgePending' },
  applied: { label: 'Applied', className: 'badgeApplied' },
  archived: { label: 'Archived', className: 'badgeArchived' },
  rejected: { label: 'Rejected', className: 'badgeRejected' },
};

const formatPostingDate = (iso: string): string => {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const SearchBox: React.FC<SearchBoxProps> = ({ gridArea, gridRow, gridColumn }) => {
  const navigate = useNavigate();

  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);

  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const { state, retry } = useJobSearch(query);

  const trimmed = query.trim();
  const tooShort = trimmed.length > 0 && trimmed.length < 2;

  // Keep the last successful payload so the panel can show prior results while a
  // new query is loading, instead of flashing a skeleton on every keystroke.
  // Written in an effect (not during render) so it's StrictMode-safe.
  const lastSuccess = useRef<{ results: JobSearchResult[]; count: number; total: number; query: string } | null>(null);
  useEffect(() => {
    if (state.status === 'success') {
      lastSuccess.current = { results: state.results, count: state.count, total: state.total, query: state.query };
    }
    // Drop the cache once the query is cleared/too short so reopening is clean.
    if (trimmed.length < 2) {
      lastSuccess.current = null;
    }
  }, [state, trimmed]);

  // The rows currently rendered (current results, or the retained previous set
  // during a reload). This is also what keyboard navigation operates on.
  const visibleResults = useMemo(() => {
    if (state.status === 'success') return state.results;
    if (state.status === 'loading' && lastSuccess.current) return lastSuccess.current.results;
    return [];
  }, [state]);

  const showPanel = open && trimmed.length > 0;

  // Reset the keyboard highlight the instant the query changes, so a stale
  // highlight from the previous result set can't be selected (e.g. via Enter)
  // while the next search is still loading.
  useEffect(() => {
    setActiveIndex(-1);
  }, [query]);

  // Close the panel on an outside click (mirrors the header's other menus).
  // touchstart included: iOS doesn't reliably synthesize mousedown for taps
  // on non-interactive elements.
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, []);

  const selectResult = (result: JobSearchResult) => {
    navigate(searchResultRoute(result));
    setQuery('');
    setOpen(false);
    inputRef.current?.blur();
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setQuery(event.target.value);
    setOpen(true);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Escape') {
      setOpen(false);
      inputRef.current?.blur();
      return;
    }

    if (!visibleResults.length) {
      return;
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setOpen(true);
      setActiveIndex((i) => (i + 1) % visibleResults.length);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setOpen(true);
      setActiveIndex((i) => (i <= 0 ? visibleResults.length - 1 : i - 1));
    } else if (event.key === 'Enter') {
      event.preventDefault();
      const pick = activeIndex >= 0 ? visibleResults[activeIndex] : visibleResults[0];
      if (pick) selectResult(pick);
    }
  };

  const styles: React.CSSProperties = {};
  if (gridArea) {
    styles.gridArea = gridArea;
  } else {
    if (gridRow !== undefined) styles.gridRow = gridRow;
    if (gridColumn !== undefined) styles.gridColumn = gridColumn;
  }

  // Footer summary uses whichever payload is currently rendered.
  const footerData =
    state.status === 'success'
      ? { count: state.count, total: state.total }
      : state.status === 'loading' && lastSuccess.current
        ? { count: lastSuccess.current.count, total: lastSuccess.current.total }
        : null;

  const renderResultRow = (result: JobSearchResult, index: number) => {
    const badge = STATUS_BADGE[result.status];
    return (
      <button
        key={`${result.id}-${result.status}`}
        type="button"
        id={optionId(index)}
        role="option"
        aria-selected={index === activeIndex}
        className={`${css.resultRow} ${index === activeIndex ? css.resultRowActive : ''}`}
        // onMouseDown (not onClick) so the navigation fires before the input's
        // blur closes the panel.
        onMouseDown={(e) => {
          e.preventDefault();
          selectResult(result);
        }}
        onMouseEnter={() => setActiveIndex(index)}
      >
        <span className={css.resultMain}>
          <span className={css.resultTitle}>{result.title}</span>
          <span className={css.resultCompany}>{result.companyName ?? 'Unknown company'}</span>
        </span>
        <span className={css.resultMeta}>
          <span className={`${css.badge} ${css[badge.className]}`}>{badge.label}</span>
          {formatPostingDate(result.jobPostingDate) && (
            <span className={css.resultDate}>{formatPostingDate(result.jobPostingDate)}</span>
          )}
        </span>
      </button>
    );
  };

  const renderPanelBody = () => {
    if (tooShort) {
      return <div className={css.hint}>Keep typing — search needs at least 2 characters.</div>;
    }

    if (state.status === 'error') {
      return (
        <div className={css.statusBlock}>
          <p className={css.statusText}>Search is having trouble. Please try again.</p>
          <button type="button" className={css.retryButton} onClick={retry}>
            Try again
          </button>
        </div>
      );
    }

    // Loading with no prior results to show → skeleton rows.
    if (state.status === 'loading' && !lastSuccess.current) {
      return (
        <div className={css.skeletonList} aria-hidden="true">
          {[0, 1, 2].map((i) => (
            <div key={i} className={css.skeletonRow}>
              <span className={css.skeletonLineLong} />
              <span className={css.skeletonLineShort} />
            </div>
          ))}
        </div>
      );
    }

    // Settled empty result set → specific empty-state copy (search is exact).
    if (state.status === 'success' && state.results.length === 0) {
      return (
        <div className={css.statusBlock}>
          <p className={css.statusText}>
            No matches for <strong>“{state.query}”</strong>.
          </p>
          <p className={css.statusSubtext}>
            Try a company name or job title — search is exact (no typo correction).
          </p>
        </div>
      );
    }

    if (visibleResults.length > 0) {
      return (
        <div
          id={LISTBOX_ID}
          role="listbox"
          aria-label="Job search results"
          className={css.resultList}
        >
          {visibleResults.map(renderResultRow)}
        </div>
      );
    }

    return null;
  };

  const panelBody = renderPanelBody();
  const activeDescendant = activeIndex >= 0 ? optionId(activeIndex) : undefined;

  return (
    <div className={css['search-box']} style={styles} ref={containerRef}>
      <input
        ref={inputRef}
        type="text"
        placeholder="Search all your jobs…"
        value={query}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onFocus={() => setOpen(true)}
        role="combobox"
        aria-expanded={showPanel && !!panelBody}
        aria-controls={LISTBOX_ID}
        aria-autocomplete="list"
        aria-activedescendant={activeDescendant}
        aria-busy={state.status === 'loading'}
        autoComplete="off"
        spellCheck={false}
      />
      <button
        type="button"
        className={css.searchIconButton}
        aria-label="Search"
        tabIndex={-1}
        onClick={() => inputRef.current?.focus()}
      >
        <FaSearch className={css['icon-image']} />
      </button>

      {showPanel && panelBody && (
        <div className={css.panel} role="presentation">
          {state.status === 'loading' && <div className={css.loadingBar} aria-hidden="true" />}
          {panelBody}
          {footerData && footerData.total > 0 && (
            <div className={css.panelFooter}>
              <span>
                Showing {footerData.count} of {footerData.total}
              </span>
              {footerData.total > footerData.count && (
                <span className={css.refineHint}>Refine your search to narrow results</span>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SearchBox;
